import { create } from 'zustand';

import type { BizyUserMessageNode } from '../../../lib/bizy/types';

// One-shot bus for injecting a message into the continuous chat from outside the
// chat view. The People Browser "Let's dive in" lives in the person view, which
// is a sibling overlay of the chat and can't call its component-local
// `sendMessage`. Queuing a message here lets the chat view (and the desktop
// chat pane) consume it and send via the normal `sendMessage` path — appending
// to the current segment, no reset / conflict dialog.
//
// Beyond plain text, a queued message can carry:
//   - `extraContext`: request context Bizy uses to act on the turn (e.g. the
//     observed moment to draft a recognition for) — not displayed.
//   - `userNode`: a structured display node so the turn renders as a chip
//     instead of the raw text (BIZY-651).

export interface BizyPendingChatMessage {
	/** Text stored/displayed for the turn and seen by the model. */
	text: string;
	/** Request context merged into the send (drives tools / drafting). */
	extraContext?: Record<string, string>;
	/** Structured display node for the user turn (renders instead of the text bubble). */
	userNode?: BizyUserMessageNode;
}

interface BizyPendingChatMessageStore {
	pendingMessage: BizyPendingChatMessage | null;
	/** Queue a message to be sent into the chat once it's mounted/visible. */
	sendChatMessage: (message: BizyPendingChatMessage) => void;
	clearPendingMessage: () => void;
}

export const useBizyPendingChatMessageStore = create<BizyPendingChatMessageStore>((set) => ({
	pendingMessage: null,
	sendChatMessage: (message) => set({ pendingMessage: message }),
	clearPendingMessage: () => set({ pendingMessage: null }),
}));
